import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Card,
    Table,
    DatePicker,
    Input,
    Select,
    Button,
    Space,
    Row,
    Col,
    Alert,
    Dropdown,
    Typography,
    message,
    Modal,
    InputNumber,
    Checkbox,
    Tag
} from 'antd';
import {
    SearchOutlined,
    DownloadOutlined,
    DeleteOutlined,
    PrinterOutlined,
    FileExcelOutlined,
    BarChartOutlined,
    EyeOutlined,
    EditOutlined,
    FileTextOutlined,
    CalculatorOutlined,
    CheckOutlined,
    PlayCircleOutlined,
    CloseCircleOutlined,
    MoreOutlined,
    InfoCircleOutlined,
    ExportOutlined,
    ScissorOutlined,
    StopOutlined,
    FilterOutlined,
    CaretUpOutlined,
    CaretDownOutlined,
    ClearOutlined
} from '@ant-design/icons';
import { useTheme } from '../../context/ThemeContext';
import { API_BASE_URL } from '../../utils/config';
import { getNumericFilters, getStringFilters, getDateFilters, formatCellValue, applyAdvancedFilter, parseValue, redirectToPivotWithData, exportToExcel, printTable, parseDate, toDayjs } from '../../utils/tableFilters';
import { trIncludes, trLower } from '../../utils/trText';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isBetween from 'dayjs/plugin/isBetween';
import FormikForm from '../form/FormikForm';
import OdemeTalebiModal from '../sozlesme/OdemeTalebiModal';
import DuzenleKisayolu from '../ortak/DuzenleKisayolu';
import './FaturaPage.css';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Chatbot from '../giris/Chatbot.jsx';

dayjs.extend(customParseFormat);
dayjs.extend(isBetween);

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const monthNames = [
    'Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
    'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'
];

const iconMap = {
    'EyeOutlined': EyeOutlined,
    'EditOutlined': EditOutlined,
    'FileTextOutlined': FileTextOutlined,
    'DownloadOutlined': DownloadOutlined,
    'CalculatorOutlined': CalculatorOutlined,
    'CheckOutlined': CheckOutlined,
    'PlayCircleOutlined': PlayCircleOutlined,
    'CloseCircleOutlined': CloseCircleOutlined,
    'InfoCircleOutlined': InfoCircleOutlined,
    'ExportOutlined': ExportOutlined,
    'ScissorOutlined': ScissorOutlined,
    'StopOutlined': StopOutlined,
    'MoreOutlined': MoreOutlined,
    'DeleteOutlined': DeleteOutlined
};

// 26.10 düzeltmesi: ysk/bsk yıl kolonları (`{yıl}_kira`, `{yıl}_bos`, `{yıl}_maliyet`)
// 2013-2028 arasında var ve YS sorguları bir önceki yılı da (prev_year) okuyor. Yıl seçicisi
// 2029/2030'u da gösterdiği için 'Unknown column' (500) alınıyordu. yilKolonlu sekmelerde
// seçilebilir yıl 2014-2028 ile sınırlanır.
const YIL_KOLON_ILK = 2014;
const YIL_KOLON_SON = 2028;

const varsayilanYil = (sekme) => {
    const buYil = new Date().getFullYear();
    return sekme && sekme.yilKolonlu ? Math.min(Math.max(buYil, YIL_KOLON_ILK), YIL_KOLON_SON) : buYil;
};

const yilSecenekleri = (sekme) => {
    const buYil = new Date().getFullYear();
    const sinirli = !!(sekme && sekme.yilKolonlu);
    const ilk = sinirli ? Math.max(YIL_KOLON_ILK, buYil - 5) : buYil - 5;
    const son = sinirli ? Math.min(YIL_KOLON_SON, buYil + 4) : buYil + 4;
    const liste = [];
    for (let yil = ilk; yil <= son; yil += 1) liste.push(yil);
    return liste;
};

// 26.10 düzeltmesi: sekme meta verisi bileşen dışında (loadData'nın bağımlılıkları sabit kalsın).
//   kaynak 'query' → POST /list/execute-query/<sqlKey> (Python SQL_QUERIES, body {params:{year, month, prev_year}});
//                    ay/yıl süzmesi sunucuda yapılır, istemcide 'Ödeme Ayı' ile ayrıca süzülmez.
//   kaynak 'sqls'  → GET /list/execute/<sqlKey> (PHP ile ortak sqls satırı; değiştirilmez).
//   rowKeyField    → satır kimliği olan key_* alanı (tekrarlanırsa sıra no eklenir, Math.random yok).
//   dateRange      → Tarih Aralığı seçicisinin varsayılanı; noDefaultDate ise varsayılan YOK ama
//                    kullanıcının seçtiği aralık uygulanır. noDateFilter → seçici gösterilmez.
//   dateFields     → aralığın uygulandığı kolonlar (ilk geçerli tarih kullanılır).
const VARSAYILAN_TARIH_ALANLARI = ['Fat. Tar.', 'Dön. Baş. Tar.', 'Tarih'];

const FATURA_SEKMELERI = [
    {
        key: 'yeni_satis',
        label: 'Faturalandırılmış Yeni Satışlar',
        sqlKey: 'yeni_satis',
        kaynak: 'sqls',
        rowKeyField: 'key_banka_fatura_duzenle',
        dateRange: { startOffset: -2, endOffset: 0, unit: 'month' }
    },
    {
        key: 'ys_sabit-fatura-talep',
        label: 'Sabit Kira Faturaları',
        sqlKey: 'ys-sabit-liste',
        kaynak: 'query',
        rowKeyField: 'key_sabit_kira_fatura',
        dateType: 'yeartime',
        yilKolonlu: true
    },
    {
        key: 'ys_bos-fatura-talep',
        label: 'Boş Kabin Faturaları',
        sqlKey: 'ys-bos-liste',
        kaynak: 'query',
        rowKeyField: 'key_bos_fatura',
        dateType: 'yeartime',
        yilKolonlu: true
    },
    {
        key: 'ys_banka-girdikce-fatura-talep',
        label: 'Banka Girdikçe Faturalar',
        sqlKey: 'ys-girdikce-liste',
        kaynak: 'query',
        rowKeyField: 'key_banka_girdikce_fatura',
        dateType: 'yeartime',
        yilKolonlu: true
    },
    {
        key: 'banka-fatura-talep',
        label: 'Banka Fatura Talep',
        sqlKey: 'banka-talep-liste',
        kaynak: 'query',
        rowKeyField: 'key_banka_fatura',
        dateType: 'yeartime'
    },
    {
        key: 'fatura-talep',
        label: 'Kesilecek Faturalar',
        sqlKey: 'fatura-talep',
        kaynak: 'sqls',
        rowKeyField: 'key_banka_fatura_kes',
        dateRange: { startOffset: -1, endOffset: 1, unit: 'month', noDefaultDate: true }
    },
    {
        key: 'gider-fatura-talep',
        label: 'Kesilecek Gider Faturaları',
        sqlKey: 'ys-gider-talep-liste',
        kaynak: 'query',
        rowKeyField: 'key_gider_fatura_kes',
        dateRange: { startOffset: -1, endOffset: 1, unit: 'month', noDefaultDate: true }
    },
    {
        key: 'pasif-fatura-talep',
        label: 'Kesilecek Pasif Faturalar',
        sqlKey: 'pasif-fatura-talep',
        kaynak: 'sqls',
        rowKeyField: 'key_banka_fatura_kes',
        dateRange: { startOffset: 0, endOffset: 1, unit: 'month', noDefaultDate: true }
    },
    {
        key: 'fatura-tahsil',
        label: 'Tahsil Edilmemiş Faturalar',
        sqlKey: 'fatura-tahsil',
        kaynak: 'sqls',
        rowKeyField: 'key_banka_fatura_tahsil',
        dateRange: { startOffset: -1, endOffset: 1, unit: 'month', noDefaultDate: true }
    },
    {
        key: 'tahsil_edilmis_fatura',
        label: 'Tahsil Edilmiş Faturalar',
        sqlKey: 'tahsil_edilmis_fatura',
        kaynak: 'sqls',
        rowKeyField: 'key_banka_fatura_duzenle',
        dateFields: ['Dön. Baş. Tar.'],
        dateRange: { startOffset: -2, endOffset: 0, unit: 'month' }
    },
    {
        // Sorgu yalnız bu ayın elektrik_ay kayıtlarını getiriyor; listede tarih aralığı
        // uygulanacak bir kolon yok (eskiden seçici görünüyor ama hiç uygulanmıyordu).
        key: 'elektrik-fatura',
        label: 'Elektrik Faturaları',
        sqlKey: 'elektrik-fatura',
        kaynak: 'sqls',
        rowKeyField: 'key_elektik_fatura',
        noDateFilter: true
    },
    {
        key: 'fatura-iade',
        label: 'İade Edilecek Faturalar',
        sqlKey: 'fatura-iade',
        kaynak: 'sqls',
        rowKeyField: 'key_banka_fatura_duzenle',
        dateRange: { startOffset: -1, endOffset: 1, unit: 'month', noDefaultDate: true }
    },
    {
        key: 'fatura-iade-edilmis',
        label: 'İade Edilmiş Faturalar',
        sqlKey: 'fatura-iade-edilmis',
        kaynak: 'sqls',
        rowKeyField: 'key_banka_fatura_duzenle',
        dateRange: { startOffset: -1, endOffset: 1, unit: 'month', noDefaultDate: true }
    }
];

/** Sekmenin VARSAYILAN tarih aralığı (noDefaultDate / noDateFilter ise yok). */
const varsayilanAralik = (sekme) => {
    if (!sekme || sekme.noDateFilter || !sekme.dateRange || sekme.dateRange.noDefaultDate) return null;
    const { startOffset, endOffset, unit } = sekme.dateRange;
    const today = dayjs();
    return [today.add(startOffset, unit), today.add(endOffset, unit)];
};

/**
 * Aralık süzgeci: kayıt, seçilen aralıkta mı? (sekmenin tarih kolonlarından ilk geçerli olan)
 * Aralık uçları bir kez hesaplanır, ilk geçerli tarihte döngüden çıkılır (eskiden her satırda
 * tüm alanlar ayrıştırılıyor ve uçlar yeniden hesaplanıyordu).
 */
const tarihAraligiSuzgeci = (sekme, aralik) => {
    const alanlar = sekme.dateFields || VARSAYILAN_TARIH_ALANLARI;
    const bas = aralik[0].startOf('day').valueOf();
    const son = aralik[1].endOf('day').valueOf();
    return (record) => {
        for (let i = 0; i < alanlar.length; i += 1) {
            const d = parseDate(record[alanlar[i]]);
            const t = d ? d.valueOf() : NaN; // isValid() yerine (Date.toString, satır başına pahalı)
            if (!isNaN(t)) return t >= bas && t <= son;
        }
        return false;
    };
};

// Görüntüden gizlenen teknik kolonlar: key_* (satır kimlikleri), _* (PHP toplu işlem), id/No/ysk_id
const teknikKolonMu = (key) => key.startsWith('key_') || key.startsWith('_') || ['id', 'No', 'ysk_id'].includes(key);

const SAYISAL_AD_IPUCLARI = ['tutar', 'fiyat', 'ücret', 'miktar', 'adet', 'kesilen', 'toplam'];
const ISO_TARIH = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?)?$/;
const TR_TARIH = /^\d{1,2}\.\d{1,2}\.\d{4}( \d{2}:\d{2}(:\d{2})?)?$/;
const tarihDegeriMi = (v) => typeof v === 'string' && (ISO_TARIH.test(v) || TR_TARIH.test(v));

const tarihAdiMi = (key) => trIncludes(key, 'tarih') ||
    (trIncludes(key, 'tar') && !trIncludes(key, 'tutar')) ||
    trIncludes(key, 'başlangıç') ||
    trIncludes(key, 'bitiş') ||
    trIncludes(key, 'start') ||
    trIncludes(key, 'end') ||
    trIncludes(key, 'dönem');

/**
 * 26.10 düzeltmesi: kolonlar tüm satırların anahtarlarından türetilir (process_fatura_data
 * boş 'Fatura Türü'nü satırdan siliyor; ilk satır eksikse kolon hiç çıkmıyordu) ve kolon tipi
 * ilk satırdan değil ilk 20 boş olmayan değerden belirlenir (ilk satırda null olan sayısal
 * kolon metin gibi sıralanıyordu).
 */
const kolonlariTuret = (rows) => {
    let sablon = rows[0] || {};
    let sablonBoyu = Object.keys(sablon).length;
    rows.forEach((r) => {
        const boy = Object.keys(r).length;
        if (boy > sablonBoyu) {
            sablon = r;
            sablonBoyu = boy;
        }
    });
    const anahtarlar = Object.keys(sablon);
    const kume = new Set(anahtarlar);
    rows.forEach((r) => Object.keys(r).forEach((k) => {
        if (!kume.has(k)) {
            kume.add(k);
            anahtarlar.push(k);
        }
    }));

    return anahtarlar
        .filter((key) => !teknikKolonMu(key))
        .map((key) => {
            const ornekler = [];
            for (let i = 0; i < rows.length && ornekler.length < 20; i += 1) {
                const v = rows[i][key];
                if (v !== null && v !== undefined && v !== '') ornekler.push(v);
            }
            const adSayisal = SAYISAL_AD_IPUCLARI.some((ipucu) => trIncludes(key, ipucu));
            // Ad ipucu ('dönem', 'tar', 'bitiş'...) değerlerle doğrulanır: 'Dönem Fatura Durumu' /
            // 'Dönem Fatura Tutarı' gibi kolonlar adında 'dönem' geçse de tarih değildir.
            const tarihGorunumlu = ornekler.filter(tarihDegeriMi).length;
            // 27.10 düzeltmesi: örnek yokken (ör. talebi henüz açılmamış ayda hep NULL gelen 'Dönem Fatura
            // Tutarı' / 'Dönem Evrak No') sayısal ya da 'evrak' adlı kolon yalnız 'dönem' ipucuyla tarih sayılmaz.
            const isDateColumn = ornekler.length > 0
                ? tarihGorunumlu === ornekler.length || (tarihAdiMi(key) && tarihGorunumlu > 0)
                : (tarihAdiMi(key) && !adSayisal && !trIncludes(key, 'evrak'));
            const isNumber = !isDateColumn && (ornekler.length > 0
                ? ornekler.every((v) => (typeof v === 'number' && !isNaN(v)) ||
                    (typeof v === 'string' && (/^[₺$€]?-?[\d.,]+$/.test(v.trim()) || adSayisal)))
                : adSayisal);
            const isString = !isNumber && !isDateColumn;
            const uniqueValues = isString
                ? [...new Set(rows.map((item) => item[key]))].filter(
                    (val) => val !== undefined && val !== null && val !== ""
                )
                : [];

            let filters = [];
            if (isNumber) {
                filters = getNumericFilters();
            } else if (isDateColumn) {
                filters = getDateFilters();
            } else {
                filters = getStringFilters();
            }

            return {
                key: key,
                title: key,
                dataIndex: key,
                isDateColumn,
                isNumber,
                isString,
                uniqueValues,
                sortable: true,
                filters,
            };
        });
};

/**
 * 26.10 düzeltmesi: rowKey her render'da Math.random() üretiyordu. Anahtar yüklemede bir kez,
 * sekmenin key_* alanından (yoksa No/id, o da yoksa sıra no) üretilir; tekrar edenlere sıra
 * no eklenir (ör. elektrik-fatura'da aynı bsk iki satır gelebiliyor).
 */
const satirAnahtarlariUret = (rows, alan) => {
    const harita = new WeakMap();
    const gorulen = new Set();
    rows.forEach((r, i) => {
        let ham = alan ? r[alan] : null;
        if (ham === null || ham === undefined || ham === '') ham = r.No ?? r.id;
        let anahtar = ham !== null && ham !== undefined && ham !== '' ? String(ham) : `#${i}`;
        if (gorulen.has(anahtar)) anahtar = `${anahtar}~${i}`;
        gorulen.add(anahtar);
        harita.set(r, anahtar);
    });
    return harita;
};

/** Hücrede gösterilen metin (arama da bunu kullanır). */
const hucreGoster = (column, value) => {
    const bicimli = formatCellValue(column.key, value);
    // Adında 'tarih/tar' geçmeyen tarih kolonları (ör. 'Kira Baş.', 'Banka Söz. Bitiş')
    // formatCellValue'da biçimlenmiyor.
    if (column.isDateColumn && bicimli === value && typeof value === 'string') {
        const d = toDayjs(value);
        if (d && d.isValid() && d.year() > 1900) return d.format('DD.MM.YYYY');
    }
    return bicimli;
};

// Arama metni: ham değer + ekrandaki biçim. formatCellValue/dayjs hücre başına pahalı
// (Tahsil Edilmiş'te ~24 bin satır × 16 kolon), bu yüzden biçim burada hızlı yoldan üretilir:
// ISO tarih → 'GG.AA.YYYY', sayı → '1.234,56'.
const TR_SAYI = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ISO_GUN = /^(\d{4})-(\d{2})-(\d{2})/;
// Metin sıralaması: localeCompare(b, 'tr') ile aynı sonuç, karşılaştırma başına yeni collator yok
const TR_SIRALAYICI = new Intl.Collator('tr');

const aramaMetni = (column, value) => {
    if (value === null || value === undefined || value === '') return '';
    const ham = String(value);
    if (column.isDateColumn) {
        const m = ISO_GUN.exec(ham);
        return m ? `${ham} ${m[3]}.${m[2]}.${m[1]}` : ham;
    }
    if (column.isNumber && typeof value === 'number' && isFinite(value)) {
        return `${ham} ${TR_SAYI.format(value)}`;
    }
    return ham;
};

const gizliKolonAnahtari = (tabKey) => `fatura_gizli_kolonlar_${tabKey}`;

// 26.10 düzeltmesi (inceleme): Excel/Yazdır'a satır kimliği key_* ve PHP toplu işlem _* kolonları
// gitmez (YS/banka sorguları sonda birden çok key_* döndürüyor; printTable yalnız son anahtarı
// atıyordu). id/No/ysk_id eskisi gibi dışa aktarımda kalır.
const disaAktarilirMi = (key) => !key.startsWith('key_') && !key.startsWith('_');

const disaAktarimAnahtarlari = (rows) => {
    const kume = new Set();
    rows.forEach((r) => Object.keys(r).forEach((k) => kume.add(k)));
    return [...kume].filter(disaAktarilirMi);
};

const disaAktarimSatirlari = (rows) => {
    const anahtarlar = disaAktarimAnahtarlari(rows);
    return rows.map((r) => {
        const satir = {};
        anahtarlar.forEach((k) => { if (k in r) satir[k] = r[k]; });
        return satir;
    });
};

// 0/1 bayrak kolonları (key_odendi vb.) sayı ya da metin gelebilir: '0' doğru sayılmasın
const bayrakMi = (v) => v === true || Number(v) === 1;
// Finans'ta silinen talep gft.odeme_talebi_id'de kalır; yeniden açılabilmesi için AÇIK talebe bakılır
// (key_odeme_talebi_acik; kolon yoksa eski davranış: bağ varsa açık sayılır)
const talepAcikMi = (r) => (r.key_odeme_talebi_acik === undefined ? !!r.key_odeme_talebi : bayrakMi(r.key_odeme_talebi_acik));

// 27.10.2026: satırdan sözleşme ve kabin kaydı (YS ysk / banka bsk) düzenleme kısayolları.
// Yeni sekmede açılır; liste ve filtreler korunur. Alan yoksa ilgili öğe gösterilmez.
const KISAYOL_ALANLARI = {
    'ys_sabit-fatura-talep': { ys: 'key_sozlesme_id', ysk: 'key_sabit_kira_fatura' },
    'ys_bos-fatura-talep': { ys: 'key_sozlesme_id', ysk: 'key_bos_fatura' },
    'ys_banka-girdikce-fatura-talep': { ys: 'key_sozlesme_id', ysk: 'key_ysk_id', bs: 'key_banka_sozlesme_id', bsk: 'key_banka_girdikce_fatura' },
    'banka-fatura-talep': { bs: 'key_banka_sozlesme_id', bsk: 'key_banka_fatura' },
    'gider-fatura-talep': { ys: 'key_sozlesme_id', ysk: 'key_ysk_eff' }
};

const KisayolMenusu = ({ record, alanlar, darkMode }) => {
    const deger = (alan) => (alan && record[alan]) || null;
    return (
        <DuzenleKisayolu ys={deger(alanlar.ys)} ysk={deger(alanlar.ysk)} bs={deger(alanlar.bs)} bsk={deger(alanlar.bsk)}
            darkMode={darkMode} />
    );
};

const ActionButtons = ({ record, actionButtons, darkMode }) => {
    // 26.10 düzeltmesi: buton başına visible(record) ve özel render(record) (ör. 'Ödeme talebi #N' etiketi)
    const gorunen = (actionButtons || []).filter(
        (button) => typeof button.visible !== 'function' || button.visible(record)
    );
    if (gorunen.length === 0) {
        return null;
    }
    const ozelDugumler = gorunen
        .filter((button) => typeof button.render === 'function')
        .map((button) => <React.Fragment key={button.key}>{button.render(record)}</React.Fragment>);
    const butonlar = gorunen.filter((button) => typeof button.render !== 'function');

    if (butonlar.length > 2) {
        const menuItems = butonlar.map(button => {
            const IconComponent = iconMap[button.icon];
            return {
                key: button.key,
                label: button.label,
                icon: IconComponent ? <IconComponent /> : null,
                onClick: () => button.onClick(record)
            };
        });

        return (
            <Space size="small">
                {ozelDugumler}
                <Dropdown
                    menu={{ items: menuItems }}
                    trigger={['click']}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        icon={<MoreOutlined />}
                        size="small"
                        className={darkMode ? 'dark-button' : ''}
                        style={{
                            color: darkMode ? 'var(--dark-text)' : undefined
                        }}
                    />
                </Dropdown>
            </Space>
        );
    }

    return (
        <Space size="small" style={{ whiteSpace: 'nowrap' }}>
            {ozelDugumler}
            {butonlar.map(button => {
                const IconComponent = iconMap[button.icon];
                return (
                    <Button
                        key={button.key}
                        type={button.type}
                        size="small"
                        icon={IconComponent ? <IconComponent /> : null}
                        onClick={() => button.onClick(record)}
                        className={darkMode ? 'dark-button' : ''}
                        style={{
                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                            color: darkMode ? 'var(--dark-text)' : undefined
                        }}
                    >
                        {button.label}
                    </Button>
                );
            })}
        </Space>
    );
};

const getFilterDropdown = (column, darkMode, handleAdvancedFilterChange, clearColumnFilter, filteredInfo) => {
    if (!column) return null;
    const currentFilter = filteredInfo[column.key] || {};
    const condition = currentFilter.condition || "choose";

    return (
        <div style={{ padding: 16, minWidth: 'min(300px, 85vw)' }}>
            <Space direction="vertical" style={{ width: '100%', backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : 'var(--color-bg)', border: `1px solid ${darkMode ? 'var(--dark-border)' : 'var(--color-theme)'}`, padding: 12}}>
                <div>
                    <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)', borderColor: darkMode ? 'var(--dark-border)' : 'var(--color-theme)' }}>
                        Filtre Şartı
                    </Text>
                    <Select
                        size="small"
                        value={condition}
                        onChange={(value) => handleAdvancedFilterChange(column.key, "condition", value)}
                        style={{ width: '100%' }}
                        className={darkMode ? 'dark-select' : ''}
                    >
                        {column.filters.map((filter) => (
                            <Option key={filter.value} value={filter.value}>
                                {filter.text}
                            </Option>
                        ))}
                    </Select>
                </div>

                {/* String değer seçimi */}
                {column.isString && condition === "select" && (
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                            Değer Seç
                        </Text>
                        <Select
                            size="small"
                            value={currentFilter.value || ""}
                            onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                            style={{ width: '100%' }}
                            placeholder="Seçiniz"
                            className={darkMode ? 'dark-select' : ''}
                        >
                            <Option value="">Seçiniz</Option>
                            {column.uniqueValues.map((val) => (
                                <Option key={val} value={val}>
                                    {val}
                                </Option>
                            ))}
                        </Select>
                    </div>
                )}

                {/* Sayısal filtreler için input alanları */}
                {column.isNumber && condition !== "choose" && condition !== "between" && (
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                            Değer
                        </Text>
                        <InputNumber
                            size="small"
                            placeholder="Değer giriniz"
                            value={currentFilter.value || ""}
                            onChange={(value) => handleAdvancedFilterChange(column.key, "value", value)}
                            style={{ width: '100%' }}
                            className={darkMode ? 'dark-input' : ''}
                        />
                    </div>
                )}

                {/* Sayısal arasında filtresi */}
                {column.isNumber && condition === "between" && (
                    <>
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Min Değer
                            </Text>
                            <InputNumber
                                size="small"
                                placeholder="Min"
                                value={currentFilter.min || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "min", value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Max Değer
                            </Text>
                            <InputNumber
                                size="small"
                                placeholder="Max"
                                value={currentFilter.max || ""}
                                onChange={(value) => handleAdvancedFilterChange(column.key, "max", value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </>
                )}

                {/* String filtreler için input alanları */}
                {column.isString && !["choose", "empty", "notEmpty", "select"].includes(condition) && (
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                            Değer
                        </Text>
                        <Input
                            size="small"
                            placeholder="Değer giriniz"
                            value={currentFilter.value || ""}
                            onChange={(e) => handleAdvancedFilterChange(column.key, "value", e.target.value)}
                            style={{ width: '100%' }}
                            className={darkMode ? 'dark-input' : ''}
                        />
                    </div>
                )}

                {/* Tarih filtreler için input alanları
                    26.10 düzeltmesi: değer 'DD.MM.YYYY' saklanır (applyAdvancedFilter iki biçimi de okur);
                    eskiden 'YYYY-MM-DD' saklanıp 'DD.MM.YYYY' diye okunduğu için tablo boşalıyordu. */}
                {column.isDateColumn && ["equals", "before", "after"].includes(condition) && (
                    <div>
                        <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                            Tarih
                        </Text>
                        <DatePicker
                            size="small"
                            value={currentFilter.value ? toDayjs(currentFilter.value) : null}
                            onChange={(date) => handleAdvancedFilterChange(column.key, "value", date ? date.format('DD.MM.YYYY') : null)}
                            style={{ width: '100%' }}
                            format="DD.MM.YYYY"
                            className={darkMode ? 'dark-date-picker' : ''}
                        />
                    </div>
                )}

                {/* Tarih arasında filtresi */}
                {column.isDateColumn && condition === "between" && (
                    <>
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Başlangıç Tarihi
                            </Text>
                            <DatePicker
                                size="small"
                                value={currentFilter.startDate ? toDayjs(currentFilter.startDate) : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "startDate", date ? date.format('DD.MM.YYYY') : null)}
                                style={{ width: '100%' }}
                                format="DD.MM.YYYY"
                                className={darkMode ? 'dark-date-picker' : ''}
                            />
                        </div>
                        <div>
                            <Text strong style={{ fontSize: '12px', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}>
                                Bitiş Tarihi
                            </Text>
                            <DatePicker
                                size="small"
                                value={currentFilter.endDate ? toDayjs(currentFilter.endDate) : null}
                                onChange={(date) => handleAdvancedFilterChange(column.key, "endDate", date ? date.format('DD.MM.YYYY') : null)}
                                style={{ width: '100%' }}
                                format="DD.MM.YYYY"
                                className={darkMode ? 'dark-date-picker' : ''}
                            />
                        </div>
                    </>
                )}

                <Button
                    size="small"
                    onClick={() => clearColumnFilter(column.key)}
                    style={{ marginTop: 8, width: '100%', borderColor: darkMode ? 'var(--dark-border)' : 'var(--color-theme)', color: darkMode ? 'var(--dark-text)' : 'var(--color-theme)' }}
                    className={darkMode ? 'dark-button' : ''}
                >
                    Filtreyi Sıfırla
                </Button>
            </Space>
        </div>
    );
};

const FaturaPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();
    const { darkMode } = useTheme();
    const [activeTab, setActiveTab] = useState(FATURA_SEKMELERI[0].key);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    // 26.10 düzeltmesi: açılış sekmesinin varsayılan aralığı seçicide de görünsün; uygulanan
    // aralık her zaman filters.dateRange (kullanıcı temizlerse varsayılan geri gelmez).
    const [filters, setFilters] = useState(() => ({
        dateRange: varsayilanAralik(FATURA_SEKMELERI[0]),
        search: '',
        durum: null,
        firma: null,
        page: 1,
        pageSize: 50,
        year: null,
        month: null
    }));
    const [pagination, setPagination] = useState({
        current: 1,
        total: 0,
        pageSize: 10
    });
    const [formModalOpen, setFormModalOpen] = useState(false);
    const [currentFormId, setCurrentFormId] = useState('');
    const [modalTitle, setModalTitle] = useState('');
    const [formTableName, setFormTableName] = useState('');
    const [formUniqueField, setFormUniqueField] = useState(null);
    const [currentUniqueValue, setCurrentUniqueValue] = useState(null);
    const [filteredInfo, setFilteredInfo] = useState({});
    const [sortedInfo, setSortedInfo] = useState({});
    const [columns, setColumns] = useState([]);
    const [formExtraData, setFormExtraData] = useState({});
    // 26.10 düzeltmesi: seçili değil GİZLİ kolonlar saklanır; sorguya sonradan eklenen
    // kolonlar (ör. YS listelerinin yeni durum kolonları) kayıtlı seçim yüzünden gizli kalmasın.
    const [gizliKolonlar, setGizliKolonlar] = useState([]);
    const [columnSelectDropdownOpen, setColumnSelectDropdownOpen] = useState(false);
    const [odemeTalebiId, setOdemeTalebiId] = useState(null);
    const handlePivotRedirectRef = useRef(null);
    const istekNoRef = useRef(0);
    const satirAnahtarRef = useRef(new WeakMap());

    useEffect(() => {
        if (darkMode) {
            document.body.setAttribute('data-theme', 'dark');
        } else {
            document.body.removeAttribute('data-theme');
        }
    }, [darkMode]);

    const activeTabData = FATURA_SEKMELERI.find(tab => tab.key === activeTab);

    const loadData = useCallback(async () => {
        const sekme = FATURA_SEKMELERI.find(tab => tab.key === activeTab);
        if (!sekme) {
            console.warn('Active tab data not found for key:', activeTab);
            return;
        }
        // Hızlı sekme/ay değişiminde eski cevabın yenisinin üzerine yazmaması için
        const istekNo = ++istekNoRef.current;
        setLoading(true);
        setError(null);

        try {
            let response;

            if (sekme.kaynak === 'query') {
                const yil = filters.year || varsayilanYil(sekme);
                const ay = filters.month || new Date().getMonth() + 1;
                response = await fetch(`${API_BASE_URL}/list/execute-query/${sekme.sqlKey}`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        params: {
                            year: yil,
                            month: ay,
                            prev_year: yil - 1
                        }
                    })
                });
            } else {
                const urlParams = new URLSearchParams();
                if (sekme.dateType === 'yeartime' && filters.year && filters.month) {
                    urlParams.append('year', filters.year);
                    urlParams.append('month', filters.month);
                }
                const url = `${API_BASE_URL}/list/execute/${sekme.sqlKey}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
                response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                });
            }
            if (istekNo !== istekNoRef.current) return;

            if (!response.ok) {
                const errorText = await response.text();
                console.error(`API Error - Status: ${response.status}, Response:`, errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }

            const allData = await response.json();
            if (istekNo !== istekNoRef.current) return;

            if (!Array.isArray(allData)) {
                console.warn('API response is not an array:', allData);
                setError('Geçersiz veri formatı - API array döndürmüyor');
                setData([]);
                return;
            }

            let rows = allData;

            // 26.10 düzeltmesi: noDefaultDate yalnız VARSAYILAN aralığı kapatır; kullanıcının
            // seçtiği aralık her sekmede uygulanır (eskiden altı sekmede yok sayılıyordu).
            const aralik = sekme.noDateFilter ? null : filters.dateRange;
            if (aralik && aralik[0] && aralik[1]) {
                rows = rows.filter(tarihAraligiSuzgeci(sekme, aralik));
            }

            satirAnahtarRef.current = satirAnahtarlariUret(rows, sekme.rowKeyField);
            if (rows.length > 0) {
                setColumns(kolonlariTuret(rows));
            }
            setData(rows);
            setPagination(prev => ({
                ...prev,
                total: rows.length
            }));

        } catch (err) {
            if (istekNo !== istekNoRef.current) return;
            console.error('Fatura data load error:', err);
            setError('Veri yüklenirken hata oluştu: ' + err.message);
            setData([]);
        } finally {
            if (istekNo === istekNoRef.current) setLoading(false);
        }
    }, [activeTab, filters.dateRange, filters.year, filters.month]);

    const handleSort = (columnKey) => {
        const column = columns.find((col) => col.key === columnKey);
        if (!column) return;

        let newOrder = "asc";
        if (sortedInfo.columnKey === columnKey) {
            if (sortedInfo.order === "asc") {
                newOrder = "desc";
            } else if (sortedInfo.order === "desc") {
                newOrder = null;
            }
        }

        setSortedInfo({
            columnKey: newOrder ? columnKey : null,
            order: newOrder,
        });
    };

    const handleAdvancedFilterChange = (columnKey, valueType, value) => {
        setFilteredInfo((prev) => ({
            ...prev,
            [columnKey]: {
                ...prev[columnKey],
                [valueType]: value,
            },
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const clearColumnFilter = (columnKey) => {
        setFilteredInfo((prev) => {
            const newFilteredInfo = { ...prev };
            delete newFilteredInfo[columnKey];
            return newFilteredInfo;
        });
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    // 26.10 düzeltmesi: genel arama yalnız istemcide (eskiden her tuşta sunucuya istek atıyordu).
    // Arama başlayınca satır başına BİR KEZ arama metni üretilir (her tuşta değil): ham değer +
    // ekranda görünen biçim ('01.10.2026', '72.319,50' gibi); teknik key_* kolonları aranmaz.
    const aramaAktif = !!(filters.search || '').trim();
    const aramaIndeksi = useMemo(() => {
        const harita = new WeakMap();
        if (!aramaAktif) return harita;
        data.forEach((record) => {
            harita.set(record, trLower(columns.map((col) => aramaMetni(col, record[col.key])).join('\u0001')));
        });
        return harita;
    }, [data, columns, aramaAktif]);

    // 26.10 düzeltmesi (inceleme): sıralama anahtarı satır başına BİR KEZ hesaplanır (eskiden her
    // karşılaştırmada iki kez tarih ayrıştırılıyordu: 2 bin satırda ~13 sn) ve sıralama arama /
    // kolon filtresinden ayrı memo'dadır: aramaya yazılan her harf yeniden sıralatmaz. Sıralama
    // kararlı olduğundan önce sırala-sonra süz, önce süz-sonra sırala ile aynı sırayı verir.
    const siraliVeri = useMemo(() => {
        if (!sortedInfo.columnKey || !sortedInfo.order || columns.length === 0) return data;
        const column = columns.find((col) => col.key === sortedInfo.columnKey);
        if (!column) return data;
        const alan = sortedInfo.columnKey;
        const yon = sortedInfo.order === "asc" ? 1 : -1;

        let anahtar;
        let karsilastir;
        if (column.isDateColumn) {
            anahtar = (v) => {
                const d = parseDate(v);
                const t = d ? d.valueOf() : NaN;
                return isNaN(t) ? null : t;
            };
            karsilastir = (a, b) => {
                if (a.k === null && b.k === null) return 0;
                if (a.k === null) return -yon;
                if (b.k === null) return yon;
                return (a.k - b.k) * yon;
            };
        } else if (column.isNumber) {
            anahtar = parseValue;
            karsilastir = (a, b) => (a.k - b.k) * yon;
        } else {
            anahtar = (v) => trLower(String(v ?? ""));
            karsilastir = (a, b) => TR_SIRALAYICI.compare(a.k, b.k) * yon;
        }

        return data
            .map((record) => ({ record, k: anahtar(record[alan]) }))
            .sort(karsilastir)
            .map((x) => x.record);
    }, [data, sortedInfo, columns]);

    const filteredAndSortedData = useMemo(() => {
        const aranan = trLower((filters.search || '').trim());
        const kolonFiltreleri = columns.length > 0
            ? Object.entries(filteredInfo).map(([key, filter]) => [key, filter, columns.find((c) => c.key === key)])
            : [];
        if (!aranan && kolonFiltreleri.length === 0) return siraliVeri;

        return siraliVeri.filter((item) => {
            const matchesSearch = !aranan || (aramaIndeksi.get(item) || '').includes(aranan);
            if (!matchesSearch) return false;
            return kolonFiltreleri.every(([key, filter, col]) => applyAdvancedFilter(item[key], filter, col));
        });
    }, [siraliVeri, aramaIndeksi, filters.search, filteredInfo, columns]);

    const handlePivotRedirect = useCallback(() => {
        const sekme = FATURA_SEKMELERI.find(tab => tab.key === activeTab);
        if (!sekme?.sqlKey) return;
        redirectToPivotWithData({
            // 26.10 düzeltmesi (inceleme): arama artık loadData'da değil; pivot da Excel/Yazdır gibi
            // ekranda görünen (arama + kolon filtresi uygulanmış) listeyi alır.
            data: filteredAndSortedData,
            filters,
            tabLabel: sekme.label || 'Fatura',
            // Parçalı yükleme GET /list/execute/<key> kullanıyor; SQL_QUERIES sekmelerinde yok
            sqlKey: sekme.kaynak === 'sqls' ? sekme.sqlKey : null,
            navigate,
            message,
            setLoading,
            apiBaseUrl: API_BASE_URL,
        });
    }, [activeTab, filteredAndSortedData, filters, navigate]);
    handlePivotRedirectRef.current = handlePivotRedirect;

    const clearAllFilters = useCallback(() => {
        setFilteredInfo({});
        setFilters(prev => ({
            ...prev,
            search: ''
        }));
        setSortedInfo({});
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    }, []);

    const getSortIcon = (columnKey) => {
        if (sortedInfo.columnKey === columnKey) {
            return sortedInfo.order === "asc" ? <CaretUpOutlined /> : <CaretDownOutlined />;
        }
        return null;
    };

    useEffect(() => {
        try {
            const kayitli = localStorage.getItem(gizliKolonAnahtari(activeTab));
            const liste = kayitli ? JSON.parse(kayitli) : [];
            setGizliKolonlar(Array.isArray(liste) ? liste : []);
        } catch (error) {
            console.error('Error loading hidden columns from storage:', error);
            setGizliKolonlar([]);
        }
    }, [activeTab]);

    const gizliKolonlariKaydet = useCallback((liste) => {
        setGizliKolonlar(liste);
        try {
            localStorage.setItem(gizliKolonAnahtari(activeTab), JSON.stringify(liste));
        } catch (error) {
            console.error('Error saving hidden columns to storage:', error);
        }
    }, [activeTab]);

    const seciliKolonlar = useMemo(
        () => columns.filter(col => !gizliKolonlar.includes(col.key)).map(col => col.key),
        [columns, gizliKolonlar]
    );

    // Bu veri setinde olmayan (başka ay/yılda gelen) kolonların gizli durumu korunur
    const digerGizliKolonlar = () => gizliKolonlar.filter(key => !columns.some(col => col.key === key));

    const handleColumnSelectionChange = (checkedValues) => {
        gizliKolonlariKaydet([
            ...digerGizliKolonlar(),
            ...columns.filter(col => !checkedValues.includes(col.key)).map(col => col.key)
        ]);
    };

    const handleSelectAllColumns = (checked) => {
        gizliKolonlariKaydet(checked
            ? digerGizliKolonlar()
            : [...digerGizliKolonlar(), ...columns.map(col => col.key)]);
    };

    const visibleColumns = useMemo(() => {
        const gorunen = columns.filter(col => !gizliKolonlar.includes(col.key));
        return gorunen.length > 0 ? gorunen : columns;
    }, [columns, gizliKolonlar]);

    const removeColumn = (columnKey) => {
        if (!gizliKolonlar.includes(columnKey)) {
            gizliKolonlariKaydet([...gizliKolonlar, columnKey]);
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }));
        setPagination(prev => ({
            ...prev,
            current: 1
        }));
    };

    const handleTabChange = (key) => {
        const newTabData = FATURA_SEKMELERI.find(tab => tab.key === key);
        const yilAy = newTabData && newTabData.dateType === 'yeartime';
        setActiveTab(key);

        setFilters({
            dateRange: varsayilanAralik(newTabData),
            search: '',
            durum: null,
            firma: null,
            page: 1,
            pageSize: 25,
            year: yilAy ? varsayilanYil(newTabData) : null,
            month: yilAy ? new Date().getMonth() + 1 : null
        });
        setPagination({
            current: 1,
            total: 0,
            pageSize: 10
        });
        setFilteredInfo({});
        setSortedInfo({});
        setColumns([]);
        setData([]);
    };

    const handlePageChange = (page, pageSize) => {
        setPagination(prev => ({
            ...prev,
            current: page,
            pageSize: pageSize || prev.pageSize
        }));
    };

    const handleOpenForm = (formId, title = 'Form', tableName = '', uniqueField = '', uniqueValue = null, extraData = {}, overrideFunction = null) => {
        setModalTitle(title);
        setCurrentFormId(formId);
        setFormTableName(tableName);
        setFormUniqueField(uniqueField);
        setCurrentUniqueValue(uniqueValue);
        setFormExtraData({
            ...extraData,
            ...(overrideFunction && { _override_function: overrideFunction })
        });
        setFormModalOpen(true);
    };

    const handleFormSuccess = () => {
        setFormModalOpen(false);
        setCurrentFormId(null);
        setFormTableName('');
        setFormUniqueField('');
        setFormExtraData({});
        loadData();
    };

    const closeFormModal = () => {
        setFormModalOpen(false);
        setFormExtraData({});
    };

    useEffect(() => {
        loadData();
    }, [loadData]);

    // --- Satır aksiyonları --------------------------------------------------------------
    const tahsilFormuAc = (record) => {
        const id = record.No;
        if (id) {
            const extraData = {
                ekleyen: user?.kullanici_no || null,
                eklenme_tarihi: new Date().toISOString().split('T')[0],
            };
            handleOpenForm(254, 'Tahsil Etme Formu', 'banka_fatura_talebi', 'id', id, extraData, 'Update');
        } else {
            message.error('ID bulunamadı');
        }
    };

    const ysDetayaGit = (id, tip, bulunamadiMesaji, record) => {
        if (id) {
            navigate(`/banka_fatura_islem/${id}?tip=${tip}&m=${filters.month || new Date().getMonth() + 1}&y=${filters.year || new Date().getFullYear()}`);
        } else {
            message.error(bulunamadiMesaji || ('ID bulunamadı. Mevcut alanlar: ' + Object.keys(record || {}).join(', ')));
        }
    };

    const faturaSil = (record) => {
        Modal.confirm({
            title: <span style={{ color: darkMode ? '#fff' : undefined }}>Silme Onayı</span>,
            content: <span style={{ color: darkMode ? '#fff' : undefined }}>Bu faturayı silmek (pasife almak) istediğinize emin misiniz?</span>,
            okText: 'Evet',
            cancelText: 'Hayır',
            okType: 'danger',
            className: darkMode ? 'dark-modal' : '',
            style: darkMode ? {
                backgroundColor: 'var(--dark-bg-secondary)',
            } : {},
            okButtonProps: {
                style: darkMode ? {
                    backgroundColor: '#ff4d4f',
                    borderColor: '#ff4d4f',
                    color: '#fff'
                } : {}
            },
            cancelButtonProps: {
                style: darkMode ? {
                    backgroundColor: 'var(--dark-bg-header)',
                    borderColor: 'var(--dark-border)',
                    color: 'var(--dark-text)'
                } : {}
            },
            onOk: async () => {
                try {
                    // Authorization başlığını utils/apiAuth.js'in global fetch sarmalayıcısı ekliyor
                    const response = await fetch(`${API_BASE_URL}/list/generic/update-active`, {
                        method: 'PUT',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify({
                            table_name: 'banka_fatura_talebi',
                            id: record.No,
                            active: 0,
                        })
                    });
                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }
                    message.success('Veri başarıyla silindi');
                    loadData();
                } catch (error) {
                    console.error('Silme işlemi sırasında hata:', error);
                    message.error('Silme işlemi başarısız: ' + error.message);
                }
            },
            onCancel: () => {
            }
        });
    };

    const duzenleButonu = {
        key: 'edit',
        label: 'Düzenle',
        type: 'default',
        icon: 'EditOutlined',
        onClick: tahsilFormuAc
    };

    const sekmeAksiyonlari = {
        'yeni_satis': [duzenleButonu],
        'ys_sabit-fatura-talep': [
            {
                key: 'view',
                label: 'Görüntüle',
                type: 'default',
                icon: 'EyeOutlined',
                onClick: (record) => ysDetayaGit(record.key_sabit_kira_fatura || record.ysk_id, 'sabit', 'ID bulunamadı')
            }
        ],
        'ys_bos-fatura-talep': [
            {
                key: 'view',
                label: 'Görüntüle',
                type: 'default',
                icon: 'EyeOutlined',
                // 26.10 düzeltmesi: sorgu key_bos_fatura döndürüyor (eskiden key_banka_fatura okunup hep 'ID bulunamadı' veriyordu)
                onClick: (record) => ysDetayaGit(record.key_bos_fatura || record.key_banka_fatura, 'bos', 'ID bulunamadı')
            }
        ],
        'ys_banka-girdikce-fatura-talep': [
            {
                key: 'view',
                label: 'Görüntüle',
                type: 'default',
                icon: 'EyeOutlined',
                onClick: (record) => ysDetayaGit(record.key_banka_girdikce_fatura || record.id || record.No, 'banka',
                    'Banka girdikçe fatura ID bulunamadı. Mevcut alanlar: ' + Object.keys(record).join(', '))
            }
        ],
        'banka-fatura-talep': [
            {
                key: 'view',
                label: 'Görüntüle',
                type: 'default',
                icon: 'EyeOutlined',
                onClick: (record) => {
                    const id = record.key_banka_fatura;
                    if (id) {
                        navigate(`/banka_fatura_talep_islem/${id}?tip=banka_talep&m=${filters.month || new Date().getMonth() + 1}&y=${filters.year || new Date().getFullYear()}`);
                    } else {
                        message.error('Banka fatura ID bulunamadı. Mevcut alanlar: ' + Object.keys(record).join(', '));
                    }
                }
            }
        ],
        // 26.10 düzeltmesi: form 252 'Kaydet' kaldırıldı ('update' küçük harf yüzünden 400 dönüyordu;
        // kesildi/ödeme talebi de yazmıyordu). Yerine yeni Fedora Finans ödeme talebi (nf_odeme_talebi).
        'gider-fatura-talep': [
            {
                key: 'odeme_talebi_var',
                visible: (record) => talepAcikMi(record),
                render: (record) => <Tag color="green" style={{ marginInlineEnd: 0 }}>Ödeme talebi #{record.key_odeme_talebi}</Tag>
            },
            {
                // Ödeme talebi olmadan 'ödendi' işaretli talep (eski akış): uç zaten 409 veriyor
                key: 'odendi',
                visible: (record) => !talepAcikMi(record) && bayrakMi(record.key_odendi),
                render: () => <Tag color="default" style={{ marginInlineEnd: 0 }}>Ödendi</Tag>
            },
            {
                key: 'odeme_talebi',
                label: 'Ödeme Talebi Oluştur',
                type: 'primary',
                icon: 'ExportOutlined',
                visible: (record) => !talepAcikMi(record) && !bayrakMi(record.key_odendi),
                onClick: (record) => {
                    if (record.key_gider_fatura_kes) {
                        setOdemeTalebiId(record.key_gider_fatura_kes);
                    } else {
                        message.error('Gider fatura talebi ID bulunamadı');
                    }
                }
            }
        ],
        // 26.10 düzeltmesi: yalnız console.log yapan 'Kaydet' kaldırıldı
        'fatura-tahsil': [
            {
                key: 'sil',
                label: 'Sil',
                type: 'default',
                icon: 'DeleteOutlined',
                onClick: faturaSil
            },
            duzenleButonu
        ],
        'tahsil_edilmis_fatura': [duzenleButonu],
        'elektrik-fatura': [
            {
                key: 'view',
                label: 'Görüntüle',
                type: 'default',
                icon: 'EyeOutlined',
                onClick: (record) => {
                    const id = record.key_elektik_fatura;
                    if (id) {
                        navigate(`/elektrik_fatura_islem/${id}?tip=elektrik&m=${filters.month || new Date().getMonth() + 1}&y=${filters.year || new Date().getFullYear()}`);
                    } else {
                        message.error('Elektrik fatura ID bulunamadı. Mevcut alanlar: ' + Object.keys(record).join(', '));
                    }
                }
            }
        ],
        'fatura-iade-edilmis': [duzenleButonu]
    };

    const kisayolAlanlari = KISAYOL_ALANLARI[activeTab];
    const aktifAksiyonlar = [
        ...(sekmeAksiyonlari[activeTab] || []),
        ...(kisayolAlanlari ? [{
            key: 'kisayol',
            render: (record) => <KisayolMenusu record={record} alanlar={kisayolAlanlari} darkMode={darkMode} />
        }] : [])
    ];

    const tabloKolonlari = [
        ...visibleColumns.map((column) => ({
            ...column,
            filters: undefined,
            filterDropdown: false,
            title: (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ flex: 1, cursor: 'pointer' }}>{column.title}</span>
                    {getSortIcon(column.key)}
                    {/* 26.10 düzeltmesi: filtre menüsü portal'da açılıyor ve React olayları portal'dan
                        başlığa kabarıyor; menüdeki her tıklama sıralamayı (sağ tık da kolon gizlemeyi)
                        tetikliyordu. Olaylar burada kesilir. */}
                    <span
                        onClick={(e) => e.stopPropagation()}
                        onContextMenu={(e) => e.stopPropagation()}
                    >
                        <Dropdown
                            dropdownRender={() => getFilterDropdown(column, darkMode, handleAdvancedFilterChange, clearColumnFilter, filteredInfo)}
                            trigger={['click']}
                            placement="bottomRight"
                        >
                            <Button
                                type="text"
                                size="small"
                                style={{
                                    marginLeft: 4,
                                    color: filteredInfo[column.key] ? (darkMode ? 'var(--dark-text)' : 'var(--color-theme)') : '#ccc'
                                }}
                                className={darkMode ? 'dark-button' : ''}
                            >
                                <FilterOutlined style={{ fontSize: 12 }} />
                            </Button>
                        </Dropdown>
                    </span>
                </div>
            ),
            onHeaderCell: () => ({
                onContextMenu: (e) => {
                    if (!e.currentTarget.contains(e.target)) return;
                    e.preventDefault();
                    removeColumn(column.key);
                },
                onClick: (e) => {
                    // Yalnız gerçek başlık tıklaması sıralar (portal içinden kabaran tıklamalar değil)
                    if (!column.sortable || !e.currentTarget.contains(e.target)) return;
                    handleSort(column.key);
                }
            }),
            dataIndex: column.key,
            key: column.key,
            width: 40,
            align: 'center',
            ellipsis: {
                showTitle: true
            },
            showSorterTooltip: false,
            render: (text) => <span>{hucreGoster(column, text)}</span>
        })),
        // 26.10 düzeltmesi: aksiyonlar son veri kolonunun yerine çiziliyordu; o kolon gizlenince
        // butonlar da kayboluyordu. Artık ayrı, sağa sabit ve gizlenemeyen bir kolon.
        ...(aktifAksiyonlar.length > 0 && visibleColumns.length > 0 ? [{
            key: '__islem',
            title: 'İşlem',
            fixed: 'right',
            align: 'center',
            render: (_, record) => (
                <ActionButtons
                    record={record}
                    actionButtons={aktifAksiyonlar}
                    darkMode={darkMode}
                />
            )
        }] : [])
    ];

    return (
        <div className={`fatura-page ${darkMode ? 'dark-theme' : ''}`}>
            {/* Filtreler */}
            <Card
                className={`fatura-filters-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <Row gutter={[16, 16]} align="middle">
                    {/* Fatura Türü Dropdown */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Fatura Türü
                            </Text>
                            <Select
                                placeholder="Fatura Türü Seçin"
                                value={activeTab}
                                onChange={(value) => handleTabChange(value)}
                                style={{ width: '100%' }}
                                className={darkMode ? 'dark-select' : ''}
                            >
                                {FATURA_SEKMELERI.map(tab => (
                                    <Option key={tab.key} value={tab.key}>
                                        {tab.label}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                    </Col>

                    {/* Tarih Aralığı veya Yıl/Ay Seçici */}
                    {(() => {
                        if (activeTabData && activeTabData.dateType === 'yeartime') {
                            return (
                                <>
                                    <Col xs={12} sm={6} md={3} lg={2}>
                                        <div className="filter-item">
                                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                Yıl
                                            </Text>
                                            <Select
                                                value={filters.year}
                                                onChange={(value) => handleFilterChange('year', value)}
                                                style={{
                                                    width: '100%',
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                                className={darkMode ? 'dark-select' : ''}
                                            >
                                                {yilSecenekleri(activeTabData).map((year) => (
                                                    <Option key={year} value={year}>{year}</Option>
                                                ))}
                                            </Select>
                                        </div>
                                    </Col>
                                    <Col xs={12} sm={6} md={3} lg={2}>
                                        <div className="filter-item">
                                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                Ay
                                            </Text>
                                            <Select
                                                value={filters.month}
                                                onChange={(value) => handleFilterChange('month', value)}
                                                style={{
                                                    width: '100%',
                                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                    color: darkMode ? 'var(--dark-text)' : undefined
                                                }}
                                                className={darkMode ? 'dark-select' : ''}
                                            >
                                                {monthNames.map((month, index) => (
                                                    <Option key={index + 1} value={index + 1}>{month}</Option>
                                                ))}
                                            </Select>
                                        </div>
                                    </Col>
                                </>
                            );
                        } else if (activeTabData && activeTabData.noDateFilter) {
                            return null;
                        } else {
                            return (
                                <Col xs={24} sm={12} md={6} lg={4}>
                                    <div className="filter-item">
                                        <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                            Tarih Aralığı
                                        </Text>
                                        <RangePicker
                                            value={filters.dateRange}
                                            onChange={(dates) => handleFilterChange('dateRange', dates)}
                                            style={{
                                                width: '100%',
                                                backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                                borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                                color: darkMode ? 'var(--dark-text)' : undefined
                                            }}
                                            placeholder={['Başlangıç', 'Bitiş']}
                                            format="DD.MM.YYYY"
                                            className={darkMode ? 'dark-date-picker' : ''}
                                            locale={{
                                                lang: {
                                                    locale: 'tr_TR',
                                                    placeholder: 'Tarih seçin',
                                                    rangePlaceholder: ['Başlangıç', 'Bitiş'],
                                                    today: 'Bugün',
                                                    now: 'Şimdi',
                                                    backToToday: 'Bugüne dön',
                                                    ok: 'Tamam',
                                                    clear: 'Temizle',
                                                    month: 'Ay',
                                                    year: 'Yıl',
                                                    timeSelect: 'Zaman seç',
                                                    dateSelect: 'Tarih seç',
                                                    monthSelect: 'Ay seç',
                                                    yearSelect: 'Yıl seç',
                                                    decadeSelect: 'On yıl seç',
                                                    yearFormat: 'YYYY',
                                                    dateFormat: 'DD.MM.YYYY',
                                                    dayFormat: 'DD',
                                                    dateTimeFormat: 'DD.MM.YYYY HH:mm:ss',
                                                    monthFormat: 'MMMM',
                                                    monthBeforeYear: true,
                                                    previousMonth: 'Önceki ay (PageUp)',
                                                    nextMonth: 'Sonraki ay (PageDown)',
                                                    previousYear: 'Önceki yıl (Control + left)',
                                                    nextYear: 'Sonraki yıl (Control + right)',
                                                    previousDecade: 'Önceki on yıl',
                                                    nextDecade: 'Sonraki on yıl',
                                                    previousCentury: 'Önceki yüzyıl',
                                                    nextCentury: 'Sonraki yüzyıl',
                                                },
                                                timePickerLocale: {
                                                    placeholder: 'Zaman seçin',
                                                },
                                            }}
                                        />
                                    </div>
                                </Col>
                            );
                        }
                    })()}

                    {/* Arama */}
                    <Col xs={24} sm={12} md={6} lg={4}>
                        <div className="filter-item">
                            <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                Arama
                            </Text>
                            <Input
                                placeholder="Arama yapın..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                prefix={<SearchOutlined style={{ color: darkMode ? 'var(--dark-text-muted)' : undefined }} />}
                                allowClear
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                                className={darkMode ? 'dark-input' : ''}
                            />
                        </div>
                    </Col>

                    {/* Aksiyon Butonları */}
                    <Col xs={24} sm={24} md={6} lg={12}>
                        <div className="filter-actions" style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            <Space wrap>
                                <Button
                                    onClick={clearAllFilters}
                                    icon={<ClearOutlined />}
                                    className={darkMode ? 'dark-button' : ''}
                                    style={{
                                        backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                        borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                        color: darkMode ? 'var(--dark-text)' : undefined
                                    }}
                                >
                                    Temizle
                                </Button>
                                <Dropdown
                                    menu={{
                                        items: [
                                            {
                                                key: 'excel',
                                                icon: <FileExcelOutlined />,
                                                label: 'Excel (.xlsx)',
                                                onClick: () => exportToExcel(disaAktarimSatirlari(filteredAndSortedData))
                                            },
                                            {
                                                key: 'print',
                                                icon: <PrinterOutlined />,
                                                label: 'Yazdır',
                                                onClick: () => printTable(filteredAndSortedData, { title: activeTabData?.label || 'Fatura Verileri', message, keys: disaAktarimAnahtarlari(filteredAndSortedData) })
                                            }
                                        ]
                                    }}
                                    trigger={['click']}
                                >
                                    <Button
                                        icon={<DownloadOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Dışa Aktar
                                    </Button>
                                </Dropdown>
                                <Dropdown
                                    open={columnSelectDropdownOpen}
                                    onOpenChange={setColumnSelectDropdownOpen}
                                    trigger={['click']}
                                    dropdownRender={() => (
                                        <div style={{
                                            padding: '2px',
                                            minWidth: '250px',
                                            maxHeight: '400px',
                                            overflowY: 'auto',
                                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : '#fff',
                                            border: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}`,
                                            borderRadius: '6px',
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
                                        }}>
                                            <div style={{ marginBottom: '8px', paddingBottom: '8px', borderBottom: `1px solid ${darkMode ? 'var(--dark-border)' : '#d9d9d9'}` }}>
                                                <Checkbox
                                                    checked={seciliKolonlar.length === columns.length && columns.length > 0}
                                                    indeterminate={seciliKolonlar.length > 0 && seciliKolonlar.length < columns.length}
                                                    onChange={(e) => handleSelectAllColumns(e.target.checked)}
                                                    style={{ color: darkMode ? 'var(--dark-text)' : undefined }}
                                                >
                                                    <Text strong style={{ color: darkMode ? 'var(--dark-text)' : undefined }}>
                                                        Tümünü Seç
                                                    </Text>
                                                </Checkbox>
                                            </div>
                                            <Checkbox.Group
                                                value={seciliKolonlar}
                                                onChange={handleColumnSelectionChange}
                                                style={{ width: '100%' }}
                                            >
                                                <Space direction="vertical" style={{ width: '100%' }}>
                                                    {columns.map(column => (
                                                        <Checkbox
                                                            key={column.key}
                                                            value={column.key}
                                                            style={{
                                                                width: '100%',
                                                                color: darkMode ? 'var(--dark-text)' : undefined
                                                            }}
                                                        >
                                                            {column.title}
                                                        </Checkbox>
                                                    ))}
                                                </Space>
                                            </Checkbox.Group>
                                        </div>
                                    )}
                                >
                                    <Button
                                        icon={<EyeOutlined />}
                                        className={darkMode ? 'dark-button' : ''}
                                        style={{
                                            backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                            borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                            color: darkMode ? 'var(--dark-text)' : undefined
                                        }}
                                    >
                                        Sütun Seç
                                    </Button>
                                </Dropdown>
                                {/* Chatbot */}
                                <Chatbot />
                            </Space>
                        </div>
                    </Col>
                </Row>
            </Card>

            {/* Tablo */}
            <Card
                className={`fatura-content-card ${darkMode ? 'dark-card' : ''}`}
                style={{
                    marginTop: 16,
                    backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined,
                    borderColor: darkMode ? 'var(--dark-border)' : undefined
                }}
            >
                <div>
                    {/* Aksiyon Butonları */}
                    <div className="fatura-actions" style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                        <Space>
                            <Button
                                icon={<BarChartOutlined />}
                                onClick={() => handlePivotRedirectRef.current?.()}
                                disabled={loading || data.length === 0}
                                className={darkMode ? 'dark-button' : ''}
                                style={{
                                    backgroundColor: darkMode ? 'var(--dark-bg-header)' : undefined,
                                    borderColor: darkMode ? 'var(--dark-border)' : undefined,
                                    color: darkMode ? 'var(--dark-text)' : undefined
                                }}
                            >
                                Pivot Tabloya Git
                            </Button>
                        </Space>
                    </div>

                    {/* Tablo */}
                    {error && (
                        <Alert
                            message="Hata"
                            description={error}
                            type="error"
                            showIcon
                            style={{ marginBottom: 16 }}
                        />
                    )}

                    <Table
                        dataSource={filteredAndSortedData}
                        loading={loading}
                        rowKey={(record) => satirAnahtarRef.current.get(record)}
                        scroll={{ x: 'max-content' }}
                        className={darkMode ? 'dark-table' : ''}
                        // 27.10: dönemi ödenmiş kalem (yeni sistem ya da eski Fedora) pasif görünür
                        rowClassName={(record) => (bayrakMi(record.key_pasif) ? 'fatura-satir-pasif' : '')}
                        pagination={{
                            current: pagination.current,
                            total: filteredAndSortedData.length,
                            pageSize: pagination.pageSize,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            showTotal: (total, range) =>
                                `${range[0]}-${range[1]} / ${total} kayıt`,
                            onChange: handlePageChange,
                            onShowSizeChange: handlePageChange,
                            pageSizeOptions: ['10', '25', '50', '100']
                        }}
                        size="small"
                        style={{
                            width: '100%',
                            backgroundColor: darkMode ? 'var(--dark-bg-secondary)' : undefined
                        }}
                        columns={tabloKolonlari}
                    />
                </div>
            </Card>

            {/* Form Modal */}
            {formModalOpen && (() => {
                return (
                    <Modal
                        title={modalTitle}
                        open={formModalOpen}
                        onCancel={closeFormModal}
                        footer={null}
                        destroyOnHidden={true}
                        width={800}
                    >
                        <div style={{ maxHeight: 600, overflowY: 'auto' }}>
                            <FormikForm
                                formId={currentFormId}
                                uniqueValue={currentUniqueValue}
                                uniqueField={formUniqueField}
                                tableName={formTableName}
                                extraData={formExtraData}
                                onSuccess={handleFormSuccess}
                            />
                        </div>
                    </Modal>
                );
            })()}

            {/* Kesilecek Gider Faturaları → yeni Fedora Finans ödeme talebi (nf_odeme_talebi) */}
            <OdemeTalebiModal
                open={odemeTalebiId != null}
                talepId={odemeTalebiId}
                onClose={() => setOdemeTalebiId(null)}
                onDone={() => {
                    setOdemeTalebiId(null);
                    loadData();
                }}
            />
        </div>
    );
};

export default FaturaPage;
